import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import moment from 'moment';
import _, { isArray } from 'lodash';
import { useSelector } from 'react-redux';
import { TimeIcon } from '../../components/Icon';
import StyleButton from '../../components/StyleButton';
import { ReactComponent as GroupIcon } from '../../static/images/Group.svg';
import { teleVisitAPI } from '../../apis';
import { LoginPop } from '../../components/Login';
import { i18n } from '../../language';
import { authNameE } from '../../utils/userFuncs';
import SearchBar from './components/SearchBar';
import TimeslotDialog from './components/TimeslotDialog';
import Schedule from './components/Schedule';
import AdvancedSearchDialog from './components/AdvancedSearchDialog';

const useStyle = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: '4.375rem',
    left: '6.25rem',
    right: '0',
    bottom: '1rem',
    margin: '1rem 3.438rem 0',
    display: 'flex',
    flexDirection: 'column',
  },
  pageTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '2.25rem',
    color: '#2e4469',
    marginBottom: '1rem',
  },
  displayScheduleBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  displayScheduleTitle: {
    fontSize: '1rem',
    color: '#7d86a9',
  },
  displayScheduleContent: {
    display: 'flex',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.875rem',
    lineHeight: '1.875rem',
    color: '#2e4469',
    marginTop: '0.5rem',
  },
  iconBox: {
    width: '1.875rem',
    height: '1.875rem',
    minWidth: '1.875rem',
    minHeight: '1.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#00a8f9',
    borderRadius: '5px',
    marginRight: '0.938rem',
  },
  groupIcon: {
    width: '1.125rem',
    height: '1.125rem',
    color: '#fff',
  },
  timeIcon: {
    fontSize: '1.5rem',
    color: '#fff',
  },
  filterTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  circle: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#bad2ff',
  },
  dashedCircle: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    border: 'dashed 1px #cdcdcd',
  },
  unavailableIcon: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
  },
  verifyingIcon: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#decfff',
  },
  time: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#aaaaaa',
    margin: '0.625rem 0',
  },
}));

const InitIcon = (className) => () => <div className={className} />;

const Televisit = () => {
  const classes = useStyle();
  const [time, setTime] = useState(moment());
  const [info, setInfo] = useState({
    scheduled: 0,
    available: 0,
    lastUpdate: null,
  });
  const [timeslotList, setTimeslotList] = useState({ day: '', data: [], robotData: [], serviceDay: 1 });
  const [showTimeslotDialog, setShowTimeslotDialog] = useState(false);
  const [showCancelListDialog, setShowCancelListDialog] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({});
  const [filterFlag, setFilterFlag] = useState('All');
  const loginInfo = useSelector((state) => state.loginInfo);
  const roleMap = useSelector((state) => state.roleMap);
  const rouList = useSelector((state) => state.rouList);
  const endDate = useMemo(() => moment().add(timeslotList.serviceDay - 1, 'd'), [timeslotList.serviceDay]);

  const scrollLocation = useRef(0);

  const getTimeslotBooking = useCallback(async () => {
    try {
      if (roleMap[authNameE.teleVisit_view]) {
        const { result } = await teleVisitAPI.getTimeslotBookingList(
          { day: moment(moment(time).format('yyyy-MM-DD 00:00'), 'yyyy-MM-DD 00:00').valueOf() },
          { isHideLoading: true }
        );

        if (moment(time).isSame(moment(), 'D')) {
          const data = {
            scheduled: result?.scheduledBooking ?? '-',
            available: result?.availableTimeslot ?? '-',
            lastUpdate: result?.lastUpdatedTime ? moment(result.lastUpdatedTime, 'x').format('yyyy-MM-DD HH:mm') : '-',
          };

          sessionStorage.setItem('TELEVISIT_NUMBER_DATA', JSON.stringify(data));

          setInfo(data);
        } else {
          const data = JSON.parse(sessionStorage.getItem('TELEVISIT_NUMBER_DATA'));

          setInfo({
            scheduled: data.scheduled,
            available: data.available,
            lastUpdate: data.lastUpdate,
          });
        }

        let robotData = result.robotList ?? [];

        while (robotData.length < 3) {
          robotData.push({});
        }

        setTimeslotList({
          day: result.day,
          data: result.timeslotBookingCardVOList,
          robotData: robotData,
          serviceDay: result.serviceDay,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }, [time, roleMap]);

  useEffect(() => {
    if (!moment(time).isSame(moment(), 'D')) {
      setTime(moment());
    }
  }, [rouList, roleMap]);
  useEffect(() => {
    if (showTimeslotDialog || !loginInfo.flag) {
      return;
    }
    (async () => {
      await getTimeslotBooking();
    })();

    const interval = setInterval(() => {
      (async () => {
        await getTimeslotBooking();
      })();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getTimeslotBooking, showTimeslotDialog]);

  const handleTime = useCallback(
    (type) => (value) => {
      scrollLocation.current = 0;
      switch (type) {
        case 'choose':
          setTime(value);
          break;
        case 'add':
          setTime((preTime) => moment(preTime).add(1, 'd'));
          break;
        case 'reduce':
          setTime((preTime) => moment(preTime).add(-1, 'd'));
          break;
        case 'today':
          setTime(moment());
          break;
        default:
          break;
      }
    },
    []
  );

  const handleDetail = useCallback(
    (open, detailInfo) => () => {
      // permission
      if (roleMap[authNameE.teleVisit_create_new_booling]) {
        setShowTimeslotDialog(open);
        setDialogInfo(open ? detailInfo : {});
      }
    },
    [roleMap[authNameE.teleVisit_create_new_booling]]
  );

  const handleAdvanceSearch = useCallback(
    (open, detailInfo) => () => {
      // permission
      if (roleMap[authNameE.teleVisit_create_new_booling]) {
        setShowCancelListDialog(open);
        setDialogInfo(open ? detailInfo : {});
      }
    },
    [roleMap[authNameE.teleVisit_create_new_booling]]
  );

  const totalMemo = useMemo(() => {
    const allData = isArray(timeslotList?.data) ? timeslotList.data : [];
    let total = {
      Scheduled: 0,
      Available: 0,
      Unavailable: 0,
      Verifying: 0,
      Cancelled: 0,
    };

    total.Scheduled = _.filter(allData, (item) =>
      _.some([3, 4, 5, 9], (statusItem) => item.timeslotBookingStatusEnum === statusItem)
    ).length;

    total.Available = _.filter(allData, (item) => item.timeslotBookingStatusEnum === 1).length;

    total.Unavailable = _.filter(
      allData,
      (item) => item.timeslotBookingStatusEnum === 0 && item.bookId === null
    ).length;

    total.Verifying = _.filter(allData, (item) => item.timeslotBookingStatusEnum === 2).length;

    total.Cancelled = _.filter(allData, (item) => item.timeslotBookingStatusEnum === 0 && item.bookId !== null).length;

    return total;
  }, [timeslotList.data]);

  const filterHandler = useCallback(
    (type) => () => {
      setFilterFlag(type);
    },
    []
  );

  const timeslotMemo = useMemo(() => {
    let tempList = timeslotList;

    switch (filterFlag) {
      case 'All':
        return tempList;
      case 'Scheduled':
        return {
          ...tempList,
          data: _.filter(tempList.data, (item) =>
            _.some([3, 4, 5, 8, 9], (statusItem) => item.timeslotBookingStatusEnum === statusItem)
          ),
        };
      case 'Available':
        return {
          ...tempList,
          data: _.filter(tempList.data, (item) => item.timeslotBookingStatusEnum === 1),
        };
      case 'Unavailable':
        return {
          ...tempList,
          data: _.filter(tempList.data, (item) => item.timeslotBookingStatusEnum === 0 && item.bookId === null),
        };
      case 'Verifying':
        return {
          ...tempList,
          data: _.filter(tempList.data, (item) => item.timeslotBookingStatusEnum === 2),
        };
      default:
        return tempList;
    }
  }, [timeslotList, filterFlag]);

  const filterButtonStyle = useCallback(
    (type) =>
      filterFlag === type
        ? {
            border: 'unset',
            backgroundColor: '#232682',
            color: '#ffffff',
          }
        : {
            border: 'solid 1px #e4e7eb',
            backgroundColor: '#ffffff',
            color: '#333333',
          },
    [filterFlag]
  );

  if (!loginInfo.flag) {
    return <LoginPop />;
  }

  return (
    <div className={classes.root}>
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="#fff"
        height="100%"
        borderRadius="15px"
        p="1.25rem 1.875rem 1.25rem"
      >
        {roleMap[authNameE.teleVisit_view] && (
          <>
            <Box display="flex" justifyContent="space-between" gridGap="2rem">
              <Box
                display="flex"
                // justifyContent="space-between"
                bgcolor="#f5faff"
                borderRadius="15px"
                border="solid 1px #e4e7eb"
                p="1rem 2.5rem"
                width="25.438rem"
                alignItems="center"
              >
                <div className={classes.displayScheduleBox} style={{ marginRight: '10rem' }}>
                  <div className={classes.displayScheduleTitle}>{i18n.t('TELEVISIT_SCHEDULED_BOOKING')}</div>
                  <div className={classes.displayScheduleContent}>
                    <div className={classes.iconBox}>
                      <GroupIcon className={classes.groupIcon} />
                    </div>
                    {info.scheduled}
                  </div>
                </div>
                <div className={classes.displayScheduleBox}>
                  <div className={classes.displayScheduleTitle}>{i18n.t('TELEVISIT_AVAILABLE_TIMESLOT')}</div>
                  <div className={classes.displayScheduleContent}>
                    <div className={classes.iconBox}>
                      <TimeIcon className={classes.timeIcon} />
                    </div>
                    {info.available}
                  </div>
                </div>
              </Box>
              <Box display="flex" flexDirection="column">
                <Box display="flex" mb="0.688rem" flexDirection="row-reverse" justifyContent="space-between">
                  <SearchBar
                    onClickOption={(data) => handleDetail(true, data)()}
                    searchText={i18n.t('TELEVISIT_ADVANCE')}
                    placeholder={i18n.t('TELEVISIT_SEARCH')}
                    height="2.563rem"
                    width="47rem"
                    m="0"
                    endDate={endDate}
                    handleAdvanceSearch={handleAdvanceSearch}
                  />
                </Box>
                <Box display="flex" alignItems="center" gridGap="0.75rem" flexWrap="wrap">
                  <StyleButton
                    text={i18n.t('TELEVISIT_ALL')}
                    height="2.563rem"
                    width="unset"
                    p="0 0.625rem 0 0.625rem"
                    onClick={filterHandler('All')}
                    {...filterButtonStyle('All')}
                  />
                  <StyleButton
                    text={`${i18n.t('TELEVISIT_SCHEDULED')} (${totalMemo.Scheduled})`}
                    height="2.563rem"
                    width="unset"
                    p="0 0.625rem 0 0.625rem"
                    Icon={InitIcon(classes.circle)}
                    onClick={filterHandler('Scheduled')}
                    {...filterButtonStyle('Scheduled')}
                  />
                  <StyleButton
                    text={`${i18n.t('TELEVISIT_AVAILABLE')} (${totalMemo.Available})`}
                    height="2.563rem"
                    width="unset"
                    p="0 0.625rem 0 0.625rem"
                    Icon={InitIcon(classes.dashedCircle)}
                    onClick={filterHandler('Available')}
                    {...filterButtonStyle('Available')}
                  />
                  <StyleButton
                    text={`${i18n.t('TELEVISIT_UNAVAILABLE')} (${totalMemo.Unavailable})`}
                    height="2.563rem"
                    width="unset"
                    p="0 0.625rem 0 0.625rem"
                    Icon={InitIcon(classes.unavailableIcon)}
                    onClick={filterHandler('Unavailable')}
                    {...filterButtonStyle('Unavailable')}
                  />
                  <StyleButton
                    text={`${i18n.t('TELEVISIT_VERIFYING')} (${totalMemo.Verifying})`}
                    height="2.563rem"
                    width="unset"
                    p="0 0.625rem 0 0.625rem"
                    Icon={InitIcon(classes.verifyingIcon)}
                    onClick={filterHandler('Verifying')}
                    {...filterButtonStyle('Verifying')}
                  />
                </Box>
              </Box>
            </Box>
            <div className={classes.time}>{`${i18n.t('TELEVISIT_LAST_UPDATE')} ${info.lastUpdate ?? '-'}`}</div>
            <Schedule
              timeslotList={timeslotMemo}
              time={time}
              handleTime={handleTime}
              handleDetail={handleDetail}
              endDate={endDate}
              scrollLocation={scrollLocation}
            />
          </>
        )}
      </Box>

      {showTimeslotDialog && <TimeslotDialog handleClose={handleDetail(false)} info={dialogInfo} />}
      {showCancelListDialog && (
        <AdvancedSearchDialog handleClose={handleAdvanceSearch(false)} info={dialogInfo} time={time} />
      )}
    </div>
  );
};

export default Televisit;
